/**
 * --------------------------------------------------------
 * Premium Shopify Gallery
 * Complete / fixed version
 * --------------------------------------------------------
 */

(() => {

class PremiumGallery {

    constructor(section) {

        this.section = section;

        this.lightbox = document.getElementById(
            `gallery-lightbox-${section.dataset.sectionId}`
        );

        if (!this.lightbox) return;

        this.grid = section.querySelector('.gallery-grid');

        this.items = Array.from(
            section.querySelectorAll('[data-gallery-image]')
        );

        this.total = this.items.length;

        this.current = 0;

        this.isOpen = false;

        this.touchStartX = 0;
        this.touchEndX = 0;
        this.swipeThreshold = 50;

        this.zoom = 1;

        this.image = this.lightbox.querySelector(
            '.gallery-lightbox-image'
        );

        this.caption = this.lightbox.querySelector(
            '.gallery-lightbox-caption'
        );

        this.counter = this.lightbox.querySelector(
            '.gallery-current'
        );

        this.totalCounter = this.lightbox.querySelector(
            '.gallery-total'
        );

        this.btnPrev = this.lightbox.querySelector(
            '.gallery-prev'
        );

        this.btnNext = this.lightbox.querySelector(
            '.gallery-next'
        );

        this.btnClose = this.lightbox.querySelector(
            '.gallery-close'
        );

        this.images = this.items.map(item => ({
            full: item.dataset.full,
            alt: item.dataset.alt || '',
            caption: item.dataset.caption || ''
        }));

        // Bound refs kept so listeners can be removed in destroy()
        this._onKeyDown = this.onKeyDown.bind(this);
        this._onGridClick = this.onGridClick.bind(this);
        this._onTouchStart = this.onTouchStart.bind(this);
        this._onTouchMove = this.onTouchMove.bind(this);
        this._onTouchEnd = this.onTouchEnd.bind(this);
        this._onOverlayClick = (e) => {
            if (e.target === this.lightbox) this.close();
        };

        this.bindEvents();

    }

    bindEvents() {

        this.grid.addEventListener('click', this._onGridClick);

        this.btnClose.addEventListener('click', () => this.close());

        this.btnPrev.addEventListener('click', () => this.prev());

        this.btnNext.addEventListener('click', () => this.next());

        this.lightbox.addEventListener('click', this._onOverlayClick);

        document.addEventListener('keydown', this._onKeyDown);

        // Swipe support for mobile (touch-action:none is already set in CSS)
        this.lightbox.addEventListener('touchstart', this._onTouchStart, { passive: true });
        this.lightbox.addEventListener('touchmove', this._onTouchMove, { passive: true });
        this.lightbox.addEventListener('touchend', this._onTouchEnd);

    }

    /**
     * Removes all listeners this instance attached. Call this when a
     * section is removed/re-rendered (e.g. Shopify theme editor).
     */
    destroy() {
        document.removeEventListener('keydown', this._onKeyDown);
        this.grid.removeEventListener('click', this._onGridClick);
        this.lightbox.removeEventListener('click', this._onOverlayClick);
        this.lightbox.removeEventListener('touchstart', this._onTouchStart);
        this.lightbox.removeEventListener('touchmove', this._onTouchMove);
        this.lightbox.removeEventListener('touchend', this._onTouchEnd);
    }

    onGridClick(event) {

        const target = event.target.closest(
            '[data-gallery-image]'
        );

        if (!target) return;

        event.preventDefault();

        // Derive the index from the actual items array instead of trusting
        // a hand-written data-index attribute (which can drift out of sync,
        // causing the wrong image to open).
        const index = this.items.indexOf(target);

        if (index === -1) return;

        this.open(index);

    }

    open(index) {

        this.current = index;

        this.isOpen = true;

        document.body.classList.add('gallery-open');

        this.lightbox.classList.add('active');

        this.lightbox.setAttribute('aria-hidden', 'false');

        this.loadImage(index);

        this.trapFocus();

    }

    close() {

        this.isOpen = false;

        document.body.classList.remove('gallery-open');

        this.lightbox.classList.remove('active');

        this.lightbox.setAttribute('aria-hidden', 'true');

        this.image.removeAttribute('src');

        this.zoom = 1;

        this.restoreFocus();

    }

    loadImage(index) {

        const data = this.images[index];

        if (!data) return;

        this.image.style.opacity = 0;

        const loader = new Image();

        loader.onload = () => {

            this.image.src = data.full;

            this.image.alt = data.alt;

            requestAnimationFrame(() => {
                this.image.style.opacity = 1;
            });

            if (this.caption) {
                this.caption.textContent = data.caption;
            }

            if (this.counter) {
                this.counter.textContent = index + 1;
            }

            if (this.totalCounter) {
                this.totalCounter.textContent = this.total;
            }

            this.preload();

        };

        loader.onerror = () => {
            // Don't leave the lightbox stuck at opacity:0 forever
            this.image.alt = 'Image failed to load';
            this.image.style.opacity = 1;
        };

        loader.src = data.full;

    }

    preload() {

        if (this.total < 2) return;

        const next = (this.current + 1) % this.total;

        const prev = (this.current - 1 + this.total) % this.total;

        [next, prev].forEach(index => {
            const img = new Image();
            img.src = this.images[index].full;
        });

    }

    /* ==========================================
       Navigation
    ========================================== */

    next() {

        if (!this.total) return;

        this.current++;

        if (this.current >= this.total) {
            this.current = 0;
        }

        this.transitionImage();

    }

    prev() {

        if (!this.total) return;

        this.current--;

        if (this.current < 0) {
            this.current = this.total - 1;
        }

        this.transitionImage();

    }

    transitionImage() {

        this.image.style.opacity = '0';

        this.image.style.transform = 'scale(.97)';

        window.setTimeout(() => {
            this.loadImage(this.current);
            this.image.style.transform = 'scale(1)';
        }, 150);

    }

    /* ==========================================
       Touch / Swipe
    ========================================== */

    onTouchStart(event) {
        this.touchStartX = event.changedTouches[0].screenX;
    }

    onTouchMove(event) {
        this.touchEndX = event.changedTouches[0].screenX;
    }

    onTouchEnd() {

        const delta = this.touchStartX - this.touchEndX;

        if (Math.abs(delta) < this.swipeThreshold) return;

        if (delta > 0) {
            this.next();
        } else {
            this.prev();
        }

        this.touchStartX = 0;
        this.touchEndX = 0;

    }

    /* ==========================================
       Keyboard
    ========================================== */

    onKeyDown(event) {

        if (!this.isOpen) return;

        switch (event.key) {

            case 'Escape':
                event.preventDefault();
                this.close();
                break;

            case 'ArrowRight':
                event.preventDefault();
                this.next();
                break;

            case 'ArrowLeft':
                event.preventDefault();
                this.prev();
                break;

        }

    }

    /* ==========================================
       Focus Management
    ========================================== */

    trapFocus() {
        this.btnClose.focus();
    }

    restoreFocus() {
        const button = this.items[this.current];
        if (button) {
            button.focus();
        }
    }

    /* ==========================================
       Filter support (ADDED)
       Re-scans the DOM for currently visible
       [data-gallery-image] triggers so that lightbox
       navigation/counter reflect only the images that
       match the active filter. Nothing above this
       method was changed.
    ========================================== */

    refreshItems() {

        this.items = Array.from(
            this.section.querySelectorAll('[data-gallery-image]')
        ).filter(item => {
            const article = item.closest('.gallery-item');
            return !article || !article.classList.contains('gallery-hidden');
        });

        this.total = this.items.length;

        this.images = this.items.map(item => ({
            full: item.dataset.full,
            alt: item.dataset.alt || '',
            caption: item.dataset.caption || ''
        }));

        if (this.current >= this.total) {
            this.current = 0;
        }

        if (this.totalCounter) {
            this.totalCounter.textContent = this.total;
        }

    }

}

const galleryInstances = new Map();

function initGalleries(root = document) {
    root.querySelectorAll('.gallery-section').forEach(section => {
        if (galleryInstances.has(section)) return;
        galleryInstances.set(section, new PremiumGallery(section));
    });
}

initGalleries();

// Keep the gallery working when merchants add/edit/remove the
// section in the Shopify theme editor (customizer live preview).
document.addEventListener('shopify:section:load', (event) => {
    initGalleries(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.classList && event.target.classList.contains('gallery-section')
        ? event.target
        : event.target.querySelector && event.target.querySelector('.gallery-section');

    if (!section) return;

    const instance = galleryInstances.get(section);

    if (instance) {
        instance.destroy();
        galleryInstances.delete(section);
    }
});

// Exposed (ADDED) so the filter module below can tell an existing
// PremiumGallery instance to refresh its item list after a filter
// changes which images are visible.
window.PremiumGalleryInstances = galleryInstances;

})();

/**
 * --------------------------------------------------------
 * Premium Shopify Gallery — Filter Feature (ADDED)
 * Entirely separate module. Does not modify PremiumGallery
 * above; only listens for filter button clicks, shows/hides
 * .gallery-item elements, and asks the matching PremiumGallery
 * instance to refresh so the lightbox stays in sync.
 * --------------------------------------------------------
 */

(() => {

    function initFilters(root = document) {

        root.querySelectorAll('.gallery-section').forEach(section => {

            const filterBar = section.querySelector('.gallery-filters');

            if (!filterBar || filterBar.dataset.filterBound) return;

            filterBar.dataset.filterBound = 'true';

            const buttons = Array.from(
                filterBar.querySelectorAll('.gallery-filter-btn')
            );

            const items = Array.from(
                section.querySelectorAll('.gallery-item')
            );

            buttons.forEach(btn => {
                btn.addEventListener('click', () => {
                    applyFilter(section, items, buttons, btn);
                });
            });

        });

    }

    function applyFilter(section, items, buttons, activeBtn) {

        const filter = activeBtn.dataset.filter;

        buttons.forEach(btn => {
            const isActive = btn === activeBtn;
            btn.classList.toggle('is-active', isActive);
            btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });

        let anyVisible = false;

        items.forEach(item => {

            const categories = (item.dataset.categories || '')
                .split(' ')
                .filter(Boolean);

            const matches = filter === 'all' || categories.indexOf(filter) !== -1;

            item.classList.toggle('gallery-hidden', !matches);
            item.classList.toggle('gallery-visible', matches);

            if (matches) anyVisible = true;

        });

        const emptyState = section.querySelector('.gallery-empty-state');

        if (emptyState) {
            emptyState.classList.toggle('gallery-hidden', anyVisible);
            emptyState.classList.toggle('gallery-visible', !anyVisible);
        }

        // Keep the lightbox (open or not) scoped to the visible set.
        const instance = window.PremiumGalleryInstances &&
            window.PremiumGalleryInstances.get(section);

        if (instance && typeof instance.refreshItems === 'function') {
            instance.refreshItems();
        }

    }

    initFilters();

    document.addEventListener('shopify:section:load', (event) => {
        initFilters(event.target);
    });

})();
